/**
 * 对接人邀请 · proxyInvite / proxyAccess / activate 标志归一化
 * Magic Link activate 写入 resource_grants ACL；不再依赖 Spatie「对接人」角色。
 */

function pickField (obj, ...keys) {
  if (!obj || typeof obj !== 'object') return undefined
  for (const k of keys) {
    const v = obj[k]
    if (v !== undefined && v !== null && v !== '') return v
  }
  return undefined
}

/**
 * 归一化 proxyInvite（login / phone / wechat / activate 响应）
 * @param {Record<string, unknown>|null|undefined} raw
 */
export function normalizeProxyInvite (raw) {
  const inv = raw?.proxyInvite ?? raw?.proxy_invite
  if (!inv || typeof inv !== 'object') return null

  const companyIdRaw = pickField(inv, 'companyId', 'company_id')
  const companyId = Number(companyIdRaw)
  const status = String(pickField(inv, 'status') || '').toLowerCase()
  const grantActivated = inv.grantActivated ?? inv.grant_activated
  const requiresActivate = inv.requiresActivate ?? inv.requires_activate

  const registrationId = pickField(inv, 'registrationId', 'registration_id') ?? null
  const formId = pickField(inv, 'formId', 'form_id') ?? null

  return {
    status: status || null,
    message: pickField(inv, 'message') ?? null,
    registrationId: registrationId != null ? String(registrationId) : null,
    formId: formId != null ? String(formId) : null,
    companyId: Number.isInteger(companyId) && companyId > 0 ? companyId : null,
    redirectPath: pickField(inv, 'redirectPath', 'redirect_path') ?? null,
    redirectUrl: pickField(inv, 'redirectUrl', 'redirect_url') ?? null,
    grantActivated: grantActivated === undefined ? undefined : Boolean(grantActivated),
    requiresActivate: requiresActivate === undefined ? undefined : Boolean(requiresActivate),
    activated: status === 'activated'
      || status === 'consumed'
      || grantActivated === true
  }
}

/** 归一化 POST /login、扫码 success 中的 proxyAccess */
export function normalizeProxyAccess (raw) {
  const layer = raw?.proxyAccess ?? raw?.proxy_access
  if (!layer || typeof layer !== 'object') return null
  const authorized = layer.authorized
  const formIdsRaw = layer.formIds ?? layer.form_ids
  return {
    authorized: authorized === undefined || authorized === null ? null : Boolean(authorized),
    requiresProxyInvite: Boolean(
      layer.requiresProxyInvite ?? layer.requires_proxy_invite
    ),
    message: String(layer.message ?? '').trim(),
    hint: String(layer.hint ?? '').trim(),
    formIds: Array.isArray(formIdsRaw)
      ? formIdsRaw.map((id) => String(id).trim()).filter(Boolean)
      : []
  }
}

/** 是否已获对接人 ACL 授权（provision 完成） */
export function isProxyAccessAuthorized (proxyAccess) {
  const access = normalizeProxyAccess(proxyAccess)
  return access?.authorized === true
}

/** invite 响应是否已完成激活 */
export function isProxyInviteActivated (payload) {
  const invite = normalizeProxyInvite(payload)
  if (invite?.activated) return true
  if (payload?.grantActivated === true || payload?.grant_activated === true) return true
  return isProxyAccessAuthorized(payload?.proxyAccess ?? payload)
}

/** 是否应阻止进入对接代填页 */
export function isProxyEntryBlocked (proxyAccess) {
  const access = normalizeProxyAccess(proxyAccess)
  return Boolean(access?.requiresProxyInvite)
}

/** 归一化 requiresActivate / grantActivated / proxyInvite / proxyAccess */
export function pickGrantAuthFlags (raw) {
  if (!raw || typeof raw !== 'object') {
    return {
      requiresActivate: undefined,
      grantActivated: undefined,
      proxyInvite: null,
      proxyAccess: null
    }
  }
  const requiresActivate = raw.requiresActivate ?? raw.requires_activate
  const grantActivated = raw.grantActivated ?? raw.grant_activated
  const proxyInvite = normalizeProxyInvite(raw)
  return {
    requiresActivate: requiresActivate === undefined ? undefined : Boolean(requiresActivate),
    grantActivated: grantActivated === undefined ? undefined : Boolean(grantActivated),
    proxyInvite,
    proxyAccess: normalizeProxyAccess(raw)
  }
}

/**
 * 是否仍需单独调用 POST /auth/proxy-grant/activate
 * 登录/注册/扫码带 token 时后端通常已内联 activate
 */
export function resolveRequiresGrantActivate (preview, payload = {}) {
  const merged = pickGrantAuthFlags(payload)
  if (isProxyInviteActivated(merged) || isProxyInviteActivated(payload)) return false
  if (merged.requiresActivate === false || merged.grantActivated === false) return false
  if (merged.requiresActivate === true) return true

  const fromPreview = pickGrantAuthFlags(preview || {})
  if (fromPreview.requiresActivate === true) return true

  return false
}
